<script setup>
import Layout from "@/layouts/main.vue";
import PageHeader from "@/components/page-header";
import useVuelidate from "@vuelidate/core";
import { required, helpers } from "@vuelidate/validators";
import { computed, ref, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { authHeader } from "./../../../../helpers/authservice/auth-header";
import axios from "axios";
import { useRoute } from "vue-router";

const state = ref({
  violation: "",
  lgu_id: "",
  violation_candidates: [{ violation_id: null }],
});

const rules = {
  violation: {
    required: helpers.withMessage(
      "Camera violation Name is required",
      required
    ),
  },
  lgu_id: {
    required: helpers.withMessage("LGU is required", required),
  },
  violation_candidates: {
    $each: helpers.forEach({
      violation_id: {
        required: helpers.withMessage("Tagged violation is required", required),
      },
    }),
  },
};

const route = useRoute();
const store = useStore();
const v$ = useVuelidate(rules, state);
const loading = computed(() => store.state.globals.loading);

const title = ref("Camera Captured Violation");
const items = ref([
  {
    text: "Home",
    href: "/",
  },
  {
    text: "Settings",
    href: "/settings/camera/violation/list",
    active: true,
  },
  {
    text: "Add New Camera Captured Violation",
    active: true,
  },
]);

const lguOptions = computed(() => (store ? store.state.lgu.dropdownLGUs : []));
const lguViolationOptions = ref([]);
const submitted = ref(false);
const notification = computed(() => store.state.notification);

// Guards the watcher below from wiping out the violation_candidates that
// come back from the server while we're populating the form for editing.
const isInitialLoad = ref(false);

const getLguViolation = async (id) => {
  try {
    const response = await axios.get(
      "/traffic_record_component/violation_penalty/lgu/" + id,
      {
        headers: authHeader(),
      }
    );

    lguViolationOptions.value = response.data.data;
  } catch (err) {
    console.log(err);
    lguViolationOptions.value = [];
  }
};

const updateViolationType = () => {
  submitted.value = true;
  v$.value.$touch();

  if (v$.value.$invalid) return;
  store.dispatch("cameraViolation/updateViolationType", state.value);
};

const getAllLGUs = async () => {
  await store.dispatch("lgu/getDropdownLGUs");
};

const getViolationType = async () => {
  store.dispatch("globals/loading", null, { root: true });
  try {
    const { data } = await axios.get(`/camera/violation/${route.params.id}`, {
      headers: authHeader(),
    });
    const violation = data.data;

    isInitialLoad.value = true;
    state.value = {
      ...violation,
    };

    // Populate the violation options for the LGU this record already
    // belongs to, so the previously tagged violation_id values render
    // correctly instead of the select showing an empty list.
    if (violation.lgu_id) {
      await getLguViolation(violation.lgu_id);
    }
    isInitialLoad.value = false;

    store.dispatch("globals/loaded", null, { root: true });
  } catch (err) {
    console.log(err);
    isInitialLoad.value = false;
    store.dispatch("globals/loaded", null, { root: true });
  }
};

watch(
  () => state.value.lgu_id,
  (lguId) => {
    // Skip the reset-and-reload below while we're populating the form from
    // the server response - that data already carries the correct
    // violation_id selections for its own lgu_id, they shouldn't be cleared.
    if (isInitialLoad.value) return;

    lguViolationOptions.value = [];
    state.value.violation_candidates.forEach((candidate) => {
      candidate.violation_id = null;
    });

    if (lguId) {
      getLguViolation(lguId);
    }
  }
);

onMounted(async () => {
  await getAllLGUs();
  await getViolationType();
});
</script>

<template>
  <Layout>
    <PageHeader
      back="/settings/camera/violation/list"
      :title="title"
      :items="items"
    />
    <div class="row">
      <div class="col lg-12">
        <b-overlay :show="loading">
          <div class="card">
            <div class="card-header">
              <h4 class="card-title mb-0">Edit Violation Type</h4>
            </div>
            <div class="card-body">
              <form @submit.prevent="updateViolationType" class="mx-2">
                <b-alert
                  v-if="notification && notification.message"
                  show
                  variant="success"
                  class="fade show"
                  role="alert"
                >
                  {{ notification.message }}
                </b-alert>

                <div v-if="notification.messages.length > 0">
                  <b-alert
                    v-for="(message, index) in notification.messages"
                    :key="index"
                    show
                    variant="danger"
                    class="fade show"
                    role="alert"
                  >
                    {{ message }}
                  </b-alert>
                </div>
                <div class="row">
                  <p class="fs-6 text text-decoration-underline">
                    Violation Information
                  </p>
                  <div class="col-6">
                    <div class="mb-3">
                      <label for="violation" class="form-label required"
                        >Camera Captured Violation Name</label
                      >
                      <input
                        autofocus
                        :class="{
                          'is-invalid': submitted && v$.violation.$error,
                        }"
                        v-model="state.violation"
                        type="text"
                        class="form-control"
                        placeholder="Enter camera captured violation name..."
                        id="violation"
                      />
                      <div
                        v-for="item of v$.violation.$errors"
                        :key="item.$uid"
                        class="invalid-feedback"
                      >
                        <span v-if="item.$message">{{ item.$message }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="col-6">
                    <div class="mb-3">
                      <label for="lguInput" class="form-label required"
                        >LGU</label
                      >
                      <select
                        :class="{ 'is-invalid': submitted && v$.lgu_id.$error }"
                        v-model="state.lgu_id"
                        id="lguInput"
                        class="form-select"
                        aria-label="Select LGU"
                      >
                        <option value="">Select LGU</option>
                        <option
                          v-for="lgu in lguOptions"
                          :key="lgu.id"
                          :value="lgu.id"
                        >
                          {{ lgu.name }}
                        </option>
                      </select>
                      <div
                        v-for="item of v$.lgu_id.$errors"
                        :key="item.$uid"
                        class="invalid-feedback"
                      >
                        <span v-if="item.$message">{{ item.$message }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="border-bottom mb-4"></div>

                <div class="row mb-3">
                  <p class="fs-6 text text-decoration-underline">
                    Tagged Violation
                  </p>

                  <b-list-group>
                    <b-list-group-item
                      v-for="(item, index) in state.violation_candidates"
                      :key="index"
                    >
                      <div class="row">
                        <div class="col-md-12 mb-3">
                          <label
                            :for="`violation_id-${index}`"
                            class="form-label required"
                            >Violation</label
                          >
                          <select
                            :class="{
                              'is-invalid':
                                submitted &&
                                v$.violation_candidates.$each.$response
                                  .$errors[index].violation_id.length,
                            }"
                            v-model="item.violation_id"
                            :id="`violation_id-${index}`"
                            class="form-select"
                            aria-label="Select Violation"
                            :disabled="!state.lgu_id"
                          >
                            <option value="">
                              {{
                                state.lgu_id
                                  ? "Select Violation"
                                  : "Select LGU first"
                              }}
                            </option>
                            <option
                              v-for="v in lguViolationOptions"
                              :key="v.id"
                              :value="v.id"
                            >
                              {{ v.name }}
                            </option>
                          </select>
                          <div
                            v-for="error of v$.violation_candidates.$each
                              .$response.$errors[index].violation_id"
                            :key="error.$uid"
                            class="invalid-feedback"
                          >
                            <span v-if="error.$message">{{
                              error.$message
                            }}</span>
                          </div>
                        </div>
                      </div>
                    </b-list-group-item>
                  </b-list-group>
                </div>

                <div class="row">
                  <div class="col-lg-12">
                    <div class="text-end">
                      <router-link
                        to="/settings/camera/violation/list"
                        type="submit"
                        class="btn btn-danger mr-3"
                      >
                        <i class="ri-close-fill fs-4 mr-1 valign-middle"></i>
                        Cancel
                      </router-link>
                      <button type="submit" class="btn btn-success">
                        <i class="ri-save-2-fill fs-4 mr-1 valign-middle"></i>
                        Update Camera Captured Violation
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </b-overlay>
      </div>
    </div>
  </Layout>
</template>